import type { AsyncStatus } from "@react-hookz/web";
import { useEffect, useState } from "react";

import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import * as DropdownDimensionHelpers from "../../components/dimensions/DropdownDimensionHelpers";
import { ContentAction } from "../../types/content";
import {
    getAdvertiserId,
    isValidAdvertiserId,
} from "../../utils/permission.utils";
import { NO_ACTIVITY_DROPDOWN_ITEM } from "../../utils/salesforce.utils";

import { useSfActivityAction } from "./useSfActivityAction";

export interface SfActivityLoaderProps {
    startDate: string;
    endDate: string;
    contentAction: ContentAction;
}

export function useSfActivityLoader({
    startDate,
    endDate,
    contentAction,
}: SfActivityLoaderProps): [AsyncStatus, DropdownItemData[]] {
    // Certain hard-coded sfid values, such as AccountAnosticWithIdentifier break the validation for sfids when we
    // send queries up to SalesForce. To work around this, invalid IDs are replaced with ""
    let advertiserId = getAdvertiserId()!;
    if (!isValidAdvertiserId()) {
        advertiserId = "";
        console.warn(
            `Current sfid is invalid to query SalesForce, sending ${advertiserId} instead.`,
        );
    }

    const [sfActivityState, sfActivityAction] = useSfActivityAction({
        advertiserId,
        startDate,
        endDate,
    });

    const [activityDropdownItems, setActivityDropdownItems] = useState<
        DropdownItemData[]
    >([NO_ACTIVITY_DROPDOWN_ITEM]);

    useEffect(() => {
        const fetchSfActivity = async () => {
            const response = await sfActivityAction.execute();

            const parsedData = DropdownDimensionHelpers.processItemMapper(
                response ?? [],
                DropdownDimensionHelpers.defaultRequestParameterMapping(
                    "activity",
                ),
            );
            if (contentAction === ContentAction.UPLOAD) {
                setActivityDropdownItems([
                    ...parsedData,
                    ...activityDropdownItems,
                ]);
            } else {
                setActivityDropdownItems([
                    ...activityDropdownItems,
                    ...parsedData,
                ]);
            }
        };
        void fetchSfActivity();
    }, []);

    return [sfActivityState.status, activityDropdownItems];
}
